

import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Menu, Building2, ChevronDown, Check } from 'lucide-react'
import { UserMenu } from './UserMenu'
import ThemeToggle from '@/components/ThemeToggle'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { useSidebar } from '@/hooks/useSidebar'
import { useOrganizations } from '@/hooks/useOrganizations'

export function TopNavigation() {
  const { toggle } = useSidebar()
  const navigate = useNavigate()
  const { organizations, currentOrg, switchOrg } = useOrganizations()
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOrgDropdownOpen(false)
      }
    }
    if (orgDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [orgDropdownOpen])

  function handleSwitchOrg(orgId: string) {
    switchOrg(orgId)
    setOrgDropdownOpen(false)
    navigate(`/organization/${orgId}/overview`)
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-[#1a1a2e] bg-white/95 dark:bg-[#0a0a1a]/95 backdrop-blur">
      <div className="flex h-14 items-center justify-between">
        {/* Left: Hamburger Menu + Logo */}
        <div className="flex items-center gap-4 px-6 flex-shrink-0">
          <button
            type="button"
            onClick={toggle}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a2e] transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="text-xl font-bold text-gray-900 dark:text-white">OptraAI</span>
          </Link>

          {/* Organization Switcher */}
          {organizations.length > 0 && (
            <>
              <span className="text-gray-300 dark:text-zinc-600">/</span>
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a2e] transition-colors"
                >
                  <Building2 className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-zinc-300 max-w-[200px] truncate">
                    {currentOrg?.name || 'Select Organization'}
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {orgDropdownOpen && (
                  <div className="absolute left-0 top-full mt-1 w-72 bg-white dark:bg-[#141428] rounded-lg shadow-lg border border-gray-200 dark:border-[#252540] py-1 z-50">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-[#252540]">
                      <p className="text-xs font-medium text-gray-500 dark:text-zinc-500 uppercase tracking-wider">
                        Organizations
                      </p>
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                      {organizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => handleSwitchOrg(org.id)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-[#0d0d1a] transition-colors text-left"
                        >
                          <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-600 rounded flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {org.name}
                            </p>
                            {org.slug && (
                              <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">
                                {org.slug}
                              </p>
                            )}
                          </div>
                          {currentOrg?.id === org.id && (
                            <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Right: Theme Toggle + Notification Bell + User Menu */}
        <div className="flex items-center gap-3 px-6">
          <ThemeToggle />

          <NotificationBell />

          <UserMenu />
        </div>
      </div>
    </header>
  )
}
